import { inject, Injectable } from '@angular/core'
import { MarketStatusEnum } from '@modules/alpaca-module/market-status/enums/market-status.enum'
import { SocketStockEvEnum } from '@modules/prices-module/socket-prices/enums/socket-stock-ev.enum'
import { BehaviorSubject, Subscription } from 'rxjs'
import { LiveTickerInterface } from '@modules/stocks-module/live-ticker/interfaces/live-ticker.interface'
import { TickerPolygonInterface } from '@modules/stocks-module/general/interfaces/ticker-polygon.interface'
import { SocketStocksType } from '@modules/prices-module/socket-prices/types/socket-stocks.type'
import { MarketStatusHelperService } from '@modules/alpaca-module/market-status/services/market-status-helper.service'
import { TickerPolygonBarInterface } from '@modules/stocks-module/general/interfaces/ticker-polygon-bar.interface'
import { LiveTickerPricesInterfaces } from '@modules/stocks-module/live-ticker/interfaces/live-ticker-prices.interfaces'
import { LiveTickerOrderBookInterface } from '@modules/stocks-module/live-ticker/interfaces/live-ticker-order-book.interface'
import { SocketStockQInterface } from '@modules/prices-module/socket-prices/interfaces/socket-stock-q.interface'
import { SocketStockAInterface } from '@modules/prices-module/socket-prices/interfaces/socket-stock-a.interface'
import { SocketStockTInterface } from '@modules/prices-module/socket-prices/interfaces/socket-stock-t.interface'
import { SocketPricesStocksService } from '@modules/stocks-module/socket-prices/services/socket-prices-stocks.service'
import { StocksService } from '@modules/stocks-module/general/services/stocks.service'
import { SocketPricesHelperService } from '@modules/prices-module/socket-prices/services/socket-prices-helper.service'

@Injectable({
	providedIn: 'root',
})
export class SecondaryLiveTickerService {
	protected readonly marketStatusEnum: typeof MarketStatusEnum = MarketStatusEnum
	protected readonly socketStockEvEnum: typeof SocketStockEvEnum = SocketStockEvEnum

	public liveTicker$: BehaviorSubject<LiveTickerInterface | null> = new BehaviorSubject<LiveTickerInterface | null>(
		null,
	)
	public loadingLiveTicker$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false)

	private readonly _socketPricesHelperService: SocketPricesHelperService =
		inject<SocketPricesHelperService>(SocketPricesHelperService)
	private readonly _socketPricesStocksService: SocketPricesStocksService =
		inject<SocketPricesStocksService>(SocketPricesStocksService)
	private readonly _marketStatusHelperService: MarketStatusHelperService =
		inject<MarketStatusHelperService>(MarketStatusHelperService)
	private readonly _stocksService: StocksService = inject<StocksService>(StocksService)

	private _subscriptions: Subscription = new Subscription()

	private _tickerPolygon: TickerPolygonInterface | null = null
	private _socketStocks: SocketStocksType[] = []

	get marketStatus(): MarketStatusEnum {
		return this._marketStatusHelperService.marketStatus
	}

	get isMarketOpen(): boolean {
		return this._marketStatusHelperService.isMarketOpen
	}

	private _updateLiveTicker(): void {
		const hlvMetrics: TickerPolygonBarInterface | undefined = this._calcMetricsHLV()
		if (!hlvMetrics) return
		const { h: high = 0, l: low = 0, v: volume = 0 } = hlvMetrics

		const lastMarketBar: LiveTickerPricesInterfaces = this._calcMetrics(this._calcMetricsLastMarketBar())
		const ticker: string = this._tickerPolygon?.ticker?.ticker || ''
		const lastTradePrice: number = this._tickerPolygon?.ticker.lastTrade?.p ?? 0
		const showMarketStatusPrices: boolean = this.isMarketOpen || lastTradePrice !== 0

		this.liveTicker$.next({
			ticker,
			orderBook: this._getOrderBook(),
			hlv: { high, low, volume },
			currentPrices: this._calcMetrics(hlvMetrics),
			marketStatusPrices: showMarketStatusPrices ? lastMarketBar : null,
		})
	}

	private _getOrderBook(): LiveTickerOrderBookInterface {
		const bid: number = this._tickerPolygon?.ticker?.lastQuote?.p || this._tickerPolygon?.ticker.prevDay?.c || 0
		const ask: number = this._tickerPolygon?.ticker?.lastQuote?.P || this._tickerPolygon?.ticker.prevDay?.c || 0
		const diffPercent: number = Math.abs(((ask - bid) / bid) * 100) || 0

		const stockQ: SocketStockQInterface | undefined = this._socketPricesHelperService.getStockByEV(
			this.socketStockEvEnum.Q,
			this._socketStocks,
		) as SocketStockQInterface | undefined

		const bidSize: number = stockQ?.bs || 0
		const askSize: number = stockQ?.as || 0

		return { bid, ask, diffPercent, bidSize, askSize }
	}

	private _calcMetrics(bar: TickerPolygonBarInterface): LiveTickerPricesInterfaces {
		bar.prevDayClosePrice = bar.prevDayClosePrice || 0
		bar.c = bar.c || 0
		bar.priceChanged = bar.priceChanged || 0
		bar.pricePercentChanged = bar.pricePercentChanged || 0

		const price: number = (!bar.c ? bar.prevDayClosePrice : bar?.c) || 0

		let dollar: number = 0
		if (!bar.prevDayClosePrice || !bar.c || bar.prevDayClosePrice === bar.c) {
			dollar = bar.priceChanged
		} else {
			dollar = bar.c - bar.prevDayClosePrice
		}

		let percent: number = 0
		const diffPrice: number = bar.c / bar.prevDayClosePrice
		if (!bar.prevDayClosePrice || !bar.c || diffPrice === 1) {
			percent = bar.pricePercentChanged
		} else {
			percent = (diffPrice - 1) * 100
		}

		return {
			price: price < 1 ? +price.toFixed(4) : +price.toFixed(2),
			changeMoney: +dollar.toFixed(2),
			changePercent: +percent.toFixed(2),
		}
	}

	private _calcMetricsHLV(): TickerPolygonBarInterface | undefined {
		switch (this.marketStatus) {
			case this.marketStatusEnum.marketOpen: {
				return !this._tickerPolygon?.ticker.day.c
					? this._tickerPolygon?.ticker.prevDay
					: {
							...this._tickerPolygon?.ticker.day,
							prevDayClosePrice: this._tickerPolygon?.ticker.prevDay.c,
						}
			}
			case this.marketStatusEnum.marketClose: {
				const closePrice: number = this._tickerPolygon?.ticker.day.c
					? this._tickerPolygon.ticker.day.c
					: (this._tickerPolygon?.ticker.prevDay.c ?? 0)
				return {
					...this._tickerPolygon?.ticker.prevDay,
					c: closePrice,
					priceChanged: this._tickerPolygon?.ticker.prevChange,
					pricePercentChanged: this._tickerPolygon?.ticker.prevChangePerc,
					prevDayClosePrice: 0,
				}
			}
			case this.marketStatusEnum.preMarket:
			case this.marketStatusEnum.postMarket: {
				return {
					...this._tickerPolygon?.ticker.prevDay,
					priceChanged: this._tickerPolygon?.ticker.prevChange,
					pricePercentChanged: this._tickerPolygon?.ticker.prevChangePerc,
					prevDayClosePrice: 0,
				}
			}
			default:
				return this._tickerPolygon?.ticker.prevDay
		}
	}

	private _calcMetricsLastMarketBar(): TickerPolygonBarInterface {
		const lastTradePrice: number = this._tickerPolygon?.ticker.lastTrade?.p ?? 0

		return {
			...this._tickerPolygon?.ticker.min,
			c: lastTradePrice,
			priceChanged: this._tickerPolygon?.ticker.todaysChange,
			pricePercentChanged: this._tickerPolygon?.ticker.todaysChangePerc,
			prevDayClosePrice: 0,
		}
	}

	private _updateTickerPolygonBySocketStocks(): void {
		if (this.marketStatus === this.marketStatusEnum.marketClose || !this._tickerPolygon) return

		const stockQ: SocketStockQInterface | undefined = this._socketPricesHelperService.getStockByEV(
			this.socketStockEvEnum.Q,
			this._socketStocks,
		) as SocketStockQInterface | undefined

		const stockA: SocketStockAInterface | undefined = this._socketPricesHelperService.getStockByEV(
			this.socketStockEvEnum.A,
			this._socketStocks,
		) as SocketStockAInterface | undefined

		const stockT: SocketStockTInterface | undefined = this._socketPricesHelperService.getStockByEV(
			this.socketStockEvEnum.T,
			this._socketStocks,
		) as SocketStockTInterface | undefined

		const priorityPrice: number = stockT?.p ?? stockA?.c ?? 0

		if (!stockQ && !stockA && !stockT) return

		const updatedLastQuote: TickerPolygonBarInterface = {
			...this._tickerPolygon?.ticker.lastQuote,
			...(stockQ && {
				...stockQ,
				p: stockQ.bp,
				P: stockQ.ap,
			}),
		}

		if (!stockA && !stockT && stockQ) {
			this._tickerPolygon.ticker.lastQuote = updatedLastQuote
			return
		}

		if (this.isMarketOpen) {
			this._tickerPolygon.ticker.lastQuote = updatedLastQuote
			this._tickerPolygon.ticker.day = {
				...this._tickerPolygon?.ticker.day,
				vw: stockA?.vw,
				c: priorityPrice,
				v: this._tickerPolygon?.ticker.day.v ? this._tickerPolygon?.ticker.day.v + (stockA?.v || 0) : 0,
				t: Date.now(),
				l:
					this._tickerPolygon?.ticker.day.l && this._tickerPolygon?.ticker.day.l > (stockA?.l || 0)
						? stockA?.l
						: this._tickerPolygon?.ticker.day.l,
				h:
					this._tickerPolygon?.ticker.day.h && this._tickerPolygon?.ticker.day.h < (stockA?.h || 0)
						? stockA?.h
						: this._tickerPolygon?.ticker.day.h,
				p: stockQ?.bp,
				P: stockQ?.ap,
			}
			return
		}

		if (!this.isMarketOpen) {
			const close: number = this._tickerPolygon.ticker.day?.c
				? this._tickerPolygon.ticker.day.c
				: (this._tickerPolygon.ticker.prevDay?.c ?? 0)

			this._tickerPolygon.ticker.lastQuote = updatedLastQuote
			this._tickerPolygon.ticker.lastTrade = {
				...this._tickerPolygon?.ticker.lastTrade,
				p: priorityPrice,
			}
			this._tickerPolygon.ticker.todaysChange = !close ? 0 : priorityPrice - close
			this._tickerPolygon.ticker.todaysChangePerc = !close ? 0 : ((priorityPrice - close) / close) * 100
			this._tickerPolygon.ticker.min = {
				...this._tickerPolygon?.ticker.min,
				vw: stockA?.vw,
				c: priorityPrice,
				v: this._tickerPolygon?.ticker.min.v ? this._tickerPolygon?.ticker.min.v + (stockA?.v || 0) : 0,
				t: Date.now(),
				l:
					this._tickerPolygon?.ticker.min.l && this._tickerPolygon?.ticker.min.l > (stockA?.l || 0)
						? stockA?.l || 0
						: this._tickerPolygon?.ticker.min.l,
				h:
					this._tickerPolygon?.ticker.min.h && this._tickerPolygon?.ticker.min.h < (stockA?.h || 0)
						? stockA?.h
						: this._tickerPolygon?.ticker.min.h,
				p: stockQ?.bp,
				P: stockQ?.ap,
			}
			return
		}
	}

	public closeLiveTicker(): void {
		const ticker: string | undefined = this.liveTicker$.getValue()?.ticker
		if (ticker) {
			this._socketPricesStocksService.toggleSubTickers({
				tickers: [ticker],
				action: 'unsubscribe',
				entity: this.constructor.name,
			})
		}

		this._subscriptions.unsubscribe()
		this._subscriptions = new Subscription()

		this.liveTicker$.next(null)
	}

	public setLiveTicker(symbol: string): void {
		this.closeLiveTicker()
		this.loadingLiveTicker$.next(true)

		// ticker polygon
		const tickerPolygonSub: Subscription = this._stocksService.getTickerPolygon(symbol).subscribe(
			(response: TickerPolygonInterface): void => {
				this._tickerPolygon = response
				this._updateLiveTicker()
				this.loadingLiveTicker$.next(false)
			},
			(): void => this.loadingLiveTicker$.next(false),
		)
		this._subscriptions.add(tickerPolygonSub)

		// open socket stocks
		const socketSub: Subscription = this._socketPricesStocksService
			.openSocketStocks()
			.subscribe((response: SocketStocksType[]): void => {
				this._socketStocks = response.filter((stock: SocketStocksType) => stock.sym === symbol)
				this._updateTickerPolygonBySocketStocks()
				this._updateLiveTicker()
			})
		this._subscriptions.add(socketSub)

		this._socketPricesStocksService.toggleSubTickers({
			tickers: [symbol],
			action: 'subscribe',
			entity: this.constructor.name,
		})
	}
}
